import {
  StudentState,
  PROGRAM_MAX_WEEKS,
  STUDENT_STATE_ROUTES,
  DerivedStudentState,
  MyClassroom,
  TeacherInfo,
  CourseProgress,
} from "../types/StudentModel";
import { EapProgram } from "../types/School";

// ============================================================================
// Types (until we need them elsewhere)
// ============================================================================

/**
 * Module types for each week
 */
export type ModuleType =
  | "reading"
  | "writing"
  | "listening"
  | "speaking"
  | "grammar"
  | "vocabulary"
  | "review";

/**
 * Student profile from API response
 * This matches the actual API response structure
 */
export interface StudentProfile {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  myClassroom?: MyClassroom;
  placementTest?: {
    result?: {
      easyCorrect: number;
      hardCorrect: number;
      programName: "EAP YELLOW" | "EAP GREEN" | "EAP ACCESS";
    };
    startTime?: number;
    endTime?: number;
  };
  role?: string;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// Program Validation
// ============================================================================

/**
 * Check if a program name is valid
 */
export const isValidProgram = (
  programName: string | undefined
): programName is EapProgram => {
  if (!programName) return false;
  return Object.values(EapProgram).includes(programName as EapProgram);
};

/**
 * Get maximum weeks for a program
 */
export const getMaxWeeks = (programName: EapProgram): number => {
  return PROGRAM_MAX_WEEKS[programName];
};

/**
 * Cap week number at program maximum
 * Returns the capped value and whether capping occurred
 */
export const capWeek = (
  week: number,
  programName: EapProgram
): { cappedWeek: number; wasCapped: boolean } => {
  const maxWeeks = getMaxWeeks(programName);
  const wasCapped = week > maxWeeks;
  const cappedWeek = Math.min(Math.max(1, week), maxWeeks);

  if (wasCapped) {
    console.warn(
      `[EAP Space] Week ${week} exceeds max ${maxWeeks} for ${programName}. ` +
        `Capped to ${cappedWeek}. This indicates a backend data issue that should be fixed.`
    );
  }

  return { cappedWeek, wasCapped };
};

// ============================================================================
// Student State Derivation
// ============================================================================

/**
 * Derive student state from profile
 * Priority: INACTIVE > UNASSIGNED > INVALID_PROGRAM > ACTIVE
 */
export const getStudentState = (
  profile: StudentProfile | null | undefined
): StudentState => {
  // No profile = treat as unassigned (shouldn't happen if auth works)
  if (!profile) {
    return StudentState.UNASSIGNED;
  }

  // Check 1: Is account active?
  if (profile.isActive === false) {
    return StudentState.INACTIVE;
  }

  // Check 2: Has classroom assignment?
  if (!profile.myClassroom) {
    return StudentState.UNASSIGNED;
  }

  // Check 3: Is program name valid?
  if (!isValidProgram(profile.myClassroom.programName)) {
    return StudentState.INVALID_PROGRAM;
  }

  // All checks passed
  return StudentState.ACTIVE;
};

/**
 * Get the route for a student state
 */
export const getRouteForState = (state: StudentState): string => {
  return STUDENT_STATE_ROUTES[state];
};

/**
 * Derive complete student state with all relevant info
 * This is the main function to use in components
 */
export const deriveStudentState = (
  profile: StudentProfile | null | undefined,
  isLoading: boolean = false
): DerivedStudentState => {
  // Still loading
  if (isLoading || !profile) {
    return {
      state: StudentState.UNASSIGNED,
      isReady: !isLoading,
      classroom: null,
      programName: null,
      currentWeek: 1,
      maxWeeks: 20,
      targetRoute: STUDENT_STATE_ROUTES[StudentState.UNASSIGNED],
      teacher: null,
      weekWasCapped: false,
    };
  }

  const state = getStudentState(profile);
  const classroom = profile.myClassroom || null;
  const teacher = classroom?.teacher || null;

  // Handle non-active states
  if (state !== StudentState.ACTIVE) {
    return {
      state,
      isReady: true,
      classroom,
      programName: null,
      currentWeek: 1,
      maxWeeks: 20,
      targetRoute: STUDENT_STATE_ROUTES[state],
      teacher,
      weekWasCapped: false,
    };
  }

  // Active state - we know program is valid
  const programName = classroom!.programName as EapProgram;
  const maxWeeks = getMaxWeeks(programName);
  const rawWeek = classroom!.courseProgress?.currentWeek || 1;
  const { cappedWeek, wasCapped } = capWeek(rawWeek, programName);

  return {
    state: StudentState.ACTIVE,
    isReady: true,
    classroom,
    programName,
    currentWeek: cappedWeek,
    maxWeeks,
    targetRoute: STUDENT_STATE_ROUTES[StudentState.ACTIVE],
    teacher,
    weekWasCapped: wasCapped,
  };
};

// ============================================================================
// Week & Module Validation
// ============================================================================

/**
 * Check if a week number is accessible for the student
 */
export const canAccessWeek = (
  weekNum: number,
  currentWeek: number,
  maxWeeks: number
): boolean => {
  // Week must be between 1 and maxWeeks
  if (weekNum < 1 || weekNum > maxWeeks) {
    return false;
  }
  // Student can only access up to their current week
  return weekNum <= currentWeek;
};

/**
 * Get list of accessible weeks for navigation
 */
export const getAccessibleWeeks = (
  currentWeek: number,
  maxWeeks: number
): number[] => {
  const cappedCurrent = Math.min(currentWeek, maxWeeks);
  return Array.from({ length: cappedCurrent }, (_, i) => i + 1);
};

/**
 * Valid module types
 */
const VALID_MODULE_TYPES: ModuleType[] = [
  "reading",
  "writing",
  "listening",
  "speaking",
  "grammar",
  "vocabulary",
  "review",
];

/**
 * Check if a module type is valid
 */
export const isValidModuleType = (
  moduleType: string
): moduleType is ModuleType => {
  return VALID_MODULE_TYPES.includes(moduleType as ModuleType);
};

// ============================================================================
// Display Helpers
// ============================================================================

/**
 * Get display name for a program
 */
export const getProgramDisplayName = (programName: EapProgram): string => {
  const displayNames: Record<EapProgram, string> = {
    [EapProgram.EAP_YELLOW]: "EAP Yellow (Foundation)",
    [EapProgram.EAP_GREEN]: "EAP Green (Intermediate)",
    [EapProgram.EAP_ACCESS]: "EAP Access (Advanced)",
  };
  return displayNames[programName];
};

/**
 * Get color for a program (for UI theming)
 */
export const getProgramColor = (programName: EapProgram): string => {
  const colors: Record<EapProgram, string> = {
    [EapProgram.EAP_YELLOW]: "#FFC107",
    [EapProgram.EAP_GREEN]: "#4CAF50",
    [EapProgram.EAP_ACCESS]: "#2196F3",
  };
  return colors[programName];
};

/**
 * Calculate overall progress percentage
 */
export const calculateProgress = (
  currentWeek: number,
  maxWeeks: number,
  weekProgress: number = 0
): number => {
  const completedWeeks = Math.max(0, currentWeek - 1);
  const weekContribution = completedWeeks / maxWeeks;
  const currentWeekContribution = weekProgress / 100 / maxWeeks;
  return Math.round((weekContribution + currentWeekContribution) * 100);
};

/**
 * Format module type for display
 */
export const formatModuleType = (moduleType: ModuleType): string => {
  return moduleType.charAt(0).toUpperCase() + moduleType.slice(1);
};
